import { useCallback, useEffect, useState } from 'react';
import { api, ROLES, type Invite, type Staff, type StaffRole } from '../api';
import { STOREFRONT_HOST } from '../lib';

// Staff directory — invite / join / role-change / deactivate, with the append-only audit behind it.
export function StaffPanel({ canManage, meId, meRole }: { canManage: boolean; meId: string; meRole: StaffRole }) {
  const [staff, setStaff] = useState<Staff[] | null>(null);
  const [invites, setInvites] = useState<Invite[]>([]);
  const [inviteRole, setInviteRole] = useState<StaffRole>('ops');
  const [inviteEmail, setInviteEmail] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The invite just minted, as a link the new person opens after signing in (Join.tsx reads #invite).
  const [minted, setMinted] = useState<Invite | null>(null);
  const [copied, setCopied] = useState(false);
  const joinLink = (code: string) => `${window.location.origin}/#invite=${code}`;
  const copyLink = async (link: string) => {
    try { await navigator.clipboard.writeText(link); setCopied(true); }
    catch { setMsg('⚠ Copying was blocked. Select the link and copy it.'); }
  };

  const load = useCallback(async () => {
    setStaff(await api.staff());
    if (canManage) setInvites(await api.invites().catch(() => []));
  }, [canManage]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg(ok); await load(); }
    catch (e) { setMsg(`⚠ ${(e as Error).message}`); }
    finally { setBusy(false); }
  };

  return (
    <div className="stack">
      <h1>Staff directory</h1>
      {canManage && (
        <section className="card invite">
          <h2>Invite a teammate</h2>
          <div className="row">
            <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value as StaffRole)}>
              {ROLES.filter((r) => r !== 'admin' || meRole === 'admin').map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <input placeholder="email (optional)" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            <button disabled={busy} onClick={() => act(async () => {
              const inv = await api.invite(inviteRole, inviteEmail.trim() || undefined);
              setInviteEmail(''); setMinted(inv); setCopied(false);
            }, '')}>Mint invite</button>
          </div>
          {minted && (
            <div className="minted">
              <p>Send this link to {minted.email ?? 'the new staff member'} (role: {minted.role}). They sign in at {STOREFRONT_HOST}, then open it and join. It works once.</p>
              <div className="row">
                <input className="search mono-input" readOnly value={joinLink(minted.code)} aria-label="Invite link" onFocus={(e) => e.target.select()} />
                <button type="button" onClick={() => copyLink(joinLink(minted.code))}>{copied ? 'Copied' : 'Copy link'}</button>
              </div>
              <p className="muted small">Code {minted.code}, if they'd rather type it in.</p>
            </div>
          )}
          {invites.length > 0 && (
            <p className="open-invites">Open invites: {invites.map((i) => `${i.code}·${i.role}`).join('  ')}</p>
          )}
        </section>
      )}
      {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`}>{msg}</p>}
      <section className="card">
        <table>
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th>{canManage && <th>Manage</th>}</tr></thead>
          <tbody>
            {(staff ?? []).map((s) => {
              const editable = canManage && !(s.role === 'admin' && meRole !== 'admin');
              return (
                <tr key={s.id} className={s.active ? '' : 'inactive'}>
                  <td>{s.displayName || '—'}{s.id === meId && <span className="you">you</span>}</td>
                  <td className="mono">{s.email ?? '—'}</td>
                  <td>
                    {editable ? (
                      <select value={s.role} disabled={busy} onChange={(e) => act(() => api.patchStaff(s.id, { role: e.target.value as StaffRole }), `${s.displayName || s.email} → ${e.target.value}`)}>
                        {ROLES.filter((r) => r !== 'admin' || meRole === 'admin').map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                    ) : <span className={`pill role-${s.role}`}>{s.role}</span>}
                  </td>
                  <td>{s.active ? <span className="dot on" /> : <span className="dot off" />}{s.active ? 'active' : 'inactive'}</td>
                  {canManage && (
                    <td>
                      {editable && s.id !== meId && (
                        <button className="link" disabled={busy} onClick={() => act(() => api.patchStaff(s.id, { active: !s.active }), `${s.displayName || s.email} ${s.active ? 'deactivated' : 'reactivated'}`)}>
                          {s.active ? 'deactivate' : 'reactivate'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
        {!staff && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}
